import { useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { roastingBatchesApi, selectionLotsApi, suppliersApi, suppliesApi, warehousesApi } from '../../../services/api'
import StatusBadge from './StatusBadge'
import TableActionButton from '../../../components/TableActionButton'
import SortableTh from '../../../components/SortableTh'
import { useSortableData } from '../../../hooks/useSortableData'

const SHRINKAGE_MIN = 15
const SHRINKAGE_MAX = 25

interface Warehouse {
  id: number
  name: string
  is_active: boolean
}

interface Supplier {
  id: number
  name: string
  is_active: boolean
}

interface Supply {
  id: number
  name: string
  current_cost_per_kg: number
}

// Same helper BlendingTab.tsx/PackagingTab.tsx already use to default their
// material-cost fields from distribution.supplies' configured rate -- Tostado
// never adopted this pattern, which is exactly how it ended up defaulting
// cost_per_kg_input (the roasting *fee*) to the selection lot's green-coffee
// cost instead, silently double-counting the green-coffee cost into
// roasting_cost on top of input_cost_allocated.
function findSupplyCost(supplies: Supply[], name: string): string {
  const s = supplies.find((sup) => sup.name.toLowerCase() === name.toLowerCase())
  return s ? String(s.current_cost_per_kg) : ''
}

interface SelectionLot {
  id: number
  lot_code: string
  quality_grade: number
  kg_remaining: number
  cost_per_kg: number
  status: string
}

interface RoastingBatch {
  id: number
  batch_code: string
  source_selection_lot_id: number | null
  source_lot_code: string | null
  quality_grade: number | null
  kg_input: number
  kg_output: number | null
  shrinkage_pct: number | null
  cost_per_kg_input: number
  roasting_cost: number
  input_cost_allocated: number
  total_cost: number | null
  cost_per_kg: number | null
  kg_remaining: number | null
  status: string
  warehouse_id: number
  warehouse_name: string
  requested_date: string | null
  completed_date: string | null
  notes: string | null
  is_active: boolean
  supplier_id: number | null
  supplier_name: string | null
  direct_price_per_kg: number | null
  direct_assembly_cost: number | null
  direct_freight_cost: number | null
  direct_storage_cost: number | null
}

interface CreateForm {
  source_selection_lot_id: string
  kg_input: string
  cost_per_kg_input: string
  warehouse_id: string
  notes: string
}

const emptyCreateForm: CreateForm = {
  source_selection_lot_id: '',
  kg_input: '',
  cost_per_kg_input: '',
  warehouse_id: '',
  notes: '',
}

interface DirectForm {
  kg_purchased: string
  price_per_kg: string
  assembly_cost: string
  freight_cost: string
  storage_cost: string
  supplier_id: string
  warehouse_id: string
  status: 'at_rest' | 'in_transit'
  origin_warehouse_id: string
  transit_eta: string
  notes: string
}

const emptyDirectForm: DirectForm = {
  kg_purchased: '',
  price_per_kg: '',
  assembly_cost: '',
  freight_cost: '',
  storage_cost: '',
  supplier_id: '',
  warehouse_id: '',
  status: 'at_rest',
  origin_warehouse_id: '',
  transit_eta: '',
  notes: '',
}

interface EditForm {
  warehouse_id: string
  notes: string
  origin_warehouse_id: string
  transit_eta: string
  status: 'at_rest' | 'in_transit'
  kg_input: string
  kg_output: string
  price_per_kg: string
  assembly_cost: string
  freight_cost: string
  storage_cost: string
}

function isOutOfRange(pct: number) {
  return pct < SHRINKAGE_MIN || pct > SHRINKAGE_MAX
}

export default function RoastingTab() {
  const [batches, setBatches] = useState<RoastingBatch[]>([])
  const [selectionLots, setSelectionLots] = useState<SelectionLot[]>([])
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [supplies, setSupplies] = useState<Supply[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const [showForm, setShowForm] = useState(false)
  const [createMode, setCreateMode] = useState<'chain' | 'direct'>('chain')
  const [form, setForm] = useState<CreateForm>(emptyCreateForm)
  const [directForm, setDirectForm] = useState<DirectForm>(emptyDirectForm)
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const [editingBatch, setEditingBatch] = useState<RoastingBatch | null>(null)
  const [editForm, setEditForm] = useState<EditForm>({
    warehouse_id: '',
    notes: '',
    origin_warehouse_id: '',
    transit_eta: '',
    status: 'at_rest',
    kg_input: '',
    kg_output: '',
    price_per_kg: '',
    assembly_cost: '',
    freight_cost: '',
    storage_cost: '',
  })
  const [editSaving, setEditSaving] = useState(false)
  const [editError, setEditError] = useState('')

  const [completingBatch, setCompletingBatch] = useState<RoastingBatch | null>(null)
  const [kgOutput, setKgOutput] = useState('')
  const [completeSaving, setCompleteSaving] = useState(false)
  const [completeError, setCompleteError] = useState('')
  const [cancelingId, setCancelingId] = useState<number | null>(null)

  const fetchAll = async () => {
    try {
      setLoadError('')
      const [batchesRes, selRes, whRes, suppliersRes, suppliesRes] = await Promise.all([
        roastingBatchesApi.list(),
        selectionLotsApi.list({ status: 'at_rest' }),
        warehousesApi.list(),
        suppliersApi.list(),
        suppliesApi.list(),
      ])
      setBatches(batchesRes.data.batches || [])
      setSelectionLots((selRes.data.lots || []).filter((l: SelectionLot) => l.kg_remaining > 0))
      setWarehouses(whRes.data.warehouses || [])
      setSuppliers(suppliersRes.data.suppliers || [])
      setSupplies(suppliesRes.data.supplies || [])
    } catch (err) {
      console.error('Error fetching roasting batches:', err)
      setLoadError('No se pudieron cargar los lotes de tostado. Intenta recargar la página.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
  }, [])

  const activeWarehouses = useMemo(() => warehouses.filter((w) => w.is_active), [warehouses])

  const filteredBatches = useMemo(() => {
    if (!statusFilter) return batches
    return batches.filter((b) => b.status === statusFilter)
  }, [batches, statusFilter])

  const { sortedData: sortedBatches, sortConfig, requestSort } = useSortableData(filteredBatches)

  const stats = useMemo(() => {
    const completed = batches.filter((b) => b.shrinkage_pct !== null && b.shrinkage_pct !== undefined)
    if (completed.length === 0) return null
    const values = completed.map((b) => Number(b.shrinkage_pct))
    const avg = values.reduce((sum, v) => sum + v, 0) / values.length
    const min = Math.min(...values)
    const max = Math.max(...values)
    const outOfRange = values.filter((v) => isOutOfRange(v)).length
    return { count: completed.length, avg, min, max, outOfRange }
  }, [batches])

  const selectedSourceLot = useMemo(
    () => selectionLots.find((l) => String(l.id) === form.source_selection_lot_id),
    [selectionLots, form.source_selection_lot_id]
  )

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const handleSourceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const id = e.target.value
    setForm({
      ...form,
      source_selection_lot_id: id,
      // Was wrongly defaulting to the selection lot's green-coffee cost
      // (lot.cost_per_kg) -- that's already carried forward separately via
      // input_cost_allocated, so sending it again here as cost_per_kg_input
      // (the roasting *fee*) double-counted it into roasting_cost. Default
      // to the actual 'Tostado' rate configured in Insumos instead.
      cost_per_kg_input: findSupplyCost(supplies, 'Tostado'),
    })
  }

  const openNew = () => {
    setCreateMode('chain')
    setForm({ ...emptyCreateForm, cost_per_kg_input: findSupplyCost(supplies, 'Tostado') })
    setDirectForm(emptyDirectForm)
    setError('')
    setShowForm(true)
  }

  const handleDirectChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setDirectForm({ ...directForm, [e.target.name]: e.target.value })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (createMode === 'direct') {
      const kg = parseFloat(directForm.kg_purchased)
      if (!kg || kg <= 0) {
        setError('Ingresa un peso comprado válido')
        return
      }
      if (!directForm.warehouse_id) {
        setError('Selecciona un almacén')
        return
      }
      if (directForm.status === 'in_transit' && !directForm.origin_warehouse_id) {
        setError('Selecciona el almacén de origen para una compra en tránsito')
        return
      }
      setSaving(true)
      setError('')
      try {
        await roastingBatchesApi.create({
          kg_purchased: kg,
          price_per_kg: directForm.price_per_kg ? parseFloat(directForm.price_per_kg) : undefined,
          assembly_cost: directForm.assembly_cost ? parseFloat(directForm.assembly_cost) : undefined,
          freight_cost: directForm.freight_cost ? parseFloat(directForm.freight_cost) : undefined,
          storage_cost: directForm.storage_cost ? parseFloat(directForm.storage_cost) : undefined,
          supplier_id: directForm.supplier_id ? Number(directForm.supplier_id) : undefined,
          warehouse_id: Number(directForm.warehouse_id),
          status: directForm.status,
          origin_warehouse_id:
            directForm.status === 'in_transit' && directForm.origin_warehouse_id
              ? Number(directForm.origin_warehouse_id)
              : undefined,
          transit_eta: directForm.status === 'in_transit' && directForm.transit_eta ? directForm.transit_eta : undefined,
          notes: directForm.notes || undefined,
        })
        toast.success('Compra directa de tostado registrada')
        setShowForm(false)
        fetchAll()
      } catch (err: any) {
        setError(err.response?.data?.error || 'Error al registrar la compra directa')
      } finally {
        setSaving(false)
      }
      return
    }
    if (!form.source_selection_lot_id) {
      setError('Selecciona el lote de selección de origen')
      return
    }
    const kg = parseFloat(form.kg_input)
    if (!kg || kg <= 0) {
      setError('Ingresa un peso de entrada válido')
      return
    }
    if (selectedSourceLot && kg > Number(selectedSourceLot.kg_remaining)) {
      setError(`Solo quedan ${Number(selectedSourceLot.kg_remaining).toFixed(2)} kg disponibles en este lote`)
      return
    }
    if (!form.warehouse_id) {
      setError('Selecciona un almacén')
      return
    }
    setSaving(true)
    setError('')
    try {
      await roastingBatchesApi.create({
        source_selection_lot_id: Number(form.source_selection_lot_id),
        kg_input: kg,
        cost_per_kg_input: form.cost_per_kg_input ? parseFloat(form.cost_per_kg_input) : undefined,
        warehouse_id: Number(form.warehouse_id),
        notes: form.notes || undefined,
      })
      toast.success('Lote de tostado solicitado')
      setShowForm(false)
      fetchAll()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al crear el lote de tostado')
    } finally {
      setSaving(false)
    }
  }

  const canEditKgOutput = (batch: RoastingBatch) =>
    batch.status !== 'pending' && Number(batch.kg_remaining) === Number(batch.kg_output)

  const openEdit = (batch: RoastingBatch) => {
    setEditingBatch(batch)
    setEditForm({
      warehouse_id: String(batch.warehouse_id),
      notes: batch.notes || '',
      origin_warehouse_id: '',
      transit_eta: '',
      status: batch.status === 'in_transit' ? 'in_transit' : 'at_rest',
      kg_input: String(batch.kg_input),
      kg_output: batch.kg_output !== null ? String(batch.kg_output) : '',
      price_per_kg: batch.direct_price_per_kg !== null ? String(batch.direct_price_per_kg) : '',
      assembly_cost: batch.direct_assembly_cost !== null ? String(batch.direct_assembly_cost) : '',
      freight_cost: batch.direct_freight_cost !== null ? String(batch.direct_freight_cost) : '',
      storage_cost: batch.direct_storage_cost !== null ? String(batch.direct_storage_cost) : '',
    })
    setEditError('')
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingBatch) return
    const isPending = editingBatch.status === 'pending'
    const payload: any = { warehouse_id: Number(editForm.warehouse_id), notes: editForm.notes || null }
    if (!isPending) {
      payload.origin_warehouse_id = editForm.origin_warehouse_id ? Number(editForm.origin_warehouse_id) : null
      payload.transit_eta = editForm.transit_eta || null
      payload.status = editForm.status
    }
    if (isPending) {
      const kgIn = parseFloat(editForm.kg_input)
      if (!kgIn || kgIn <= 0) {
        setEditError('Ingresa un peso de entrada válido')
        return
      }
      payload.kg_input = kgIn
    } else if (canEditKgOutput(editingBatch)) {
      const kgOut = parseFloat(editForm.kg_output)
      if (!kgOut || kgOut <= 0) {
        setEditError('Ingresa un peso de salida válido')
        return
      }
      payload.kg_output = kgOut
    }
    if (editingBatch.source_selection_lot_id === null) {
      payload.direct_price_per_kg = parseFloat(editForm.price_per_kg) || 0
      payload.direct_assembly_cost = parseFloat(editForm.assembly_cost) || 0
      payload.direct_freight_cost = parseFloat(editForm.freight_cost) || 0
      payload.direct_storage_cost = parseFloat(editForm.storage_cost) || 0
    }
    setEditSaving(true)
    setEditError('')
    try {
      await roastingBatchesApi.update(editingBatch.id, payload)
      toast.success('Lote de tostado actualizado')
      setEditingBatch(null)
      fetchAll()
    } catch (err: any) {
      setEditError(err.response?.data?.error || 'Error al actualizar el lote')
    } finally {
      setEditSaving(false)
    }
  }

  const openComplete = (batch: RoastingBatch) => {
    setCompletingBatch(batch)
    setKgOutput('')
    setCompleteError('')
  }

  const previewShrinkage = useMemo(() => {
    if (!completingBatch) return null
    const out = parseFloat(kgOutput)
    if (!out || out <= 0 || out > completingBatch.kg_input) return null
    return ((completingBatch.kg_input - out) / completingBatch.kg_input) * 100
  }, [completingBatch, kgOutput])

  const handleCompleteSubmit = async () => {
    if (!completingBatch) return
    const out = parseFloat(kgOutput)
    if (!out || out <= 0) {
      setCompleteError('Ingresa el peso tostado medido')
      return
    }
    if (out > completingBatch.kg_input) {
      setCompleteError('El peso de salida no puede ser mayor al peso de entrada')
      return
    }
    setCompleteSaving(true)
    setCompleteError('')
    try {
      await roastingBatchesApi.complete(completingBatch.id, { kg_output: out })
      const pct = ((completingBatch.kg_input - out) / completingBatch.kg_input) * 100
      if (isOutOfRange(pct)) {
        toast.error(`Tueste completado — merma ${pct.toFixed(1)}% fuera del rango esperado (15%-25%)`, { duration: 6000 })
      } else {
        toast.success(`Tueste completado — merma ${pct.toFixed(1)}%`)
      }
      setCompletingBatch(null)
      fetchAll()
    } catch (err: any) {
      setCompleteError(err.response?.data?.error || 'Error al completar el tostado')
    } finally {
      setCompleteSaving(false)
    }
  }

  const canCancel = (batch: RoastingBatch) =>
    batch.status === 'pending' ||
    (batch.status !== 'consumed' && Number(batch.kg_remaining) === Number(batch.kg_output))

  const handleCancel = async (batch: RoastingBatch) => {
    if (!confirm(`¿Anular el lote de tostado ${batch.batch_code}? Esta acción restaurará los kilos reservados en la etapa anterior.`)) return
    setCancelingId(batch.id)
    try {
      await roastingBatchesApi.cancel(batch.id)
      toast.success('Lote de tostado anulado')
      fetchAll()
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Error al anular el lote')
    } finally {
      setCancelingId(null)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      {stats && (
        <div className="bg-cream-50 shadow rounded-lg p-4 mb-4">
          <h3 className="text-sm font-semibold text-espresso-700 mb-3">Estadísticas de merma (tostados completados)</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-xs text-espresso-500 uppercase">Promedio</div>
              <div className="text-lg font-semibold text-espresso-800">{stats.avg.toFixed(1)}%</div>
            </div>
            <div>
              <div className="text-xs text-espresso-500 uppercase">Mínimo</div>
              <div className="text-lg font-semibold text-espresso-800">{stats.min.toFixed(1)}%</div>
            </div>
            <div>
              <div className="text-xs text-espresso-500 uppercase">Máximo</div>
              <div className="text-lg font-semibold text-espresso-800">{stats.max.toFixed(1)}%</div>
            </div>
            <div>
              <div className="text-xs text-espresso-500 uppercase">Fuera de rango (15%-25%)</div>
              <div className={`text-lg font-semibold ${stats.outOfRange > 0 ? 'text-amber-700' : 'text-espresso-800'}`}>
                {stats.outOfRange} / {stats.count}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <div className="flex items-center gap-3">
          <label htmlFor="roasting-status-filter" className="text-xs font-medium text-espresso-500">
            Estado
          </label>
          <select
            id="roasting-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
          >
            <option value="">Todos</option>
            <option value="pending">Pendiente</option>
            <option value="in_transit">En Tránsito</option>
            <option value="at_rest">En Almacén</option>
            <option value="consumed">Consumido</option>
          </select>
        </div>
        <button
          onClick={openNew}
          disabled={selectionLots.length === 0 || activeWarehouses.length === 0}
          title={
            selectionLots.length === 0
              ? 'No hay lotes de selección en almacén disponibles para tostar'
              : activeWarehouses.length === 0
              ? 'Crea primero un almacén activo'
              : undefined
          }
          className="px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500"
        >
          + Nuevo Tostado
        </button>
      </div>

      {selectionLots.length === 0 && (
        <div className="bg-amber-50 text-amber-800 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          No hay lotes de selección en almacén con kg disponibles. Registra una selección primero.
        </div>
      )}

      {loadError && (
        <div className="bg-red-50 text-red-700 px-4 py-3 rounded-md text-sm mb-4" role="alert">
          {loadError}
        </div>
      )}

      {!loadError && filteredBatches.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">
            {batches.length === 0 ? 'No hay lotes de tostado registrados aún.' : 'No hay lotes con este estado.'}
          </p>
        </div>
      ) : (
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <SortableTh label="Lote" sortKey="batch_code" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Fecha" sortKey="requested_date" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Origen" sortKey="source_lot_code" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Almacén" sortKey="warehouse_name" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Kg Entrada" sortKey="kg_input" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Kg Salida" sortKey="kg_output" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Merma" sortKey="shrinkage_pct" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Costo/kg" sortKey="cost_per_kg" sortConfig={sortConfig} onSort={requestSort} />
                  <SortableTh label="Estado" sortKey="status" sortConfig={sortConfig} onSort={requestSort} />
                  <th className="px-4 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {sortedBatches.map((b) => (
                  <tr key={b.id} className="hover:bg-cream-100">
                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">{b.batch_code}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                      {b.requested_date ? new Date(b.requested_date).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                      {b.source_lot_code || `Compra directa${b.supplier_name ? ` — ${b.supplier_name}` : ''}`}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">{b.warehouse_name}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">{Number(b.kg_input).toFixed(2)}</td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                      {b.kg_output !== null ? Number(b.kg_output).toFixed(2) : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm">
                      {b.shrinkage_pct !== null && b.shrinkage_pct !== undefined ? (
                        <span
                          className={`font-medium ${
                            isOutOfRange(Number(b.shrinkage_pct)) ? 'text-amber-700' : 'text-espresso-600'
                          }`}
                          title={isOutOfRange(Number(b.shrinkage_pct)) ? 'Fuera del rango esperado (15%-25%)' : undefined}
                        >
                          {Number(b.shrinkage_pct).toFixed(1)}%{isOutOfRange(Number(b.shrinkage_pct)) ? ' ⚠' : ''}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-espresso-600">
                      {b.cost_per_kg !== null ? `$${Number(b.cost_per_kg).toFixed(2)}` : '-'}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap"><StatusBadge status={b.status} isActive={b.is_active} /></td>
                    <td className="px-4 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-1">
                        {b.status !== 'consumed' && (
                          <TableActionButton onClick={() => openEdit(b)} variant="primary">
                            Editar
                          </TableActionButton>
                        )}
                        {b.status === 'pending' && (
                          <TableActionButton onClick={() => openComplete(b)} variant="success">
                            Completar
                          </TableActionButton>
                        )}
                        {b.is_active !== false && (
                          <TableActionButton
                            onClick={() => handleCancel(b)}
                            variant="danger"
                            disabled={cancelingId === b.id || !canCancel(b)}
                            title={!canCancel(b) ? 'No se puede anular: ya se generaron lotes de mezclado a partir de este lote' : undefined}
                          >
                            {cancelingId === b.id ? 'Anulando...' : 'Anular'}
                          </TableActionButton>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Nuevo Lote de Tostado</h2>
              {createMode === 'chain' && (
                <p className="text-sm text-espresso-500 mt-1">
                  Se crea como pendiente. El peso de salida se registra después con "Completar" cuando se mida la merma real.
                </p>
              )}
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm" role="alert">
                  {error}
                </div>
              )}

              <div className="flex rounded-md overflow-hidden border border-espresso-300 w-fit">
                <button
                  type="button"
                  onClick={() => setCreateMode('chain')}
                  className={`px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500 ${
                    createMode === 'chain' ? 'bg-terracotta-600 text-white' : 'bg-cream-50 text-espresso-700 hover:bg-cream-100'
                  }`}
                >
                  Producido internamente
                </button>
                <button
                  type="button"
                  onClick={() => setCreateMode('direct')}
                  className={`px-4 py-2 text-sm font-medium border-l border-espresso-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500 ${
                    createMode === 'direct' ? 'bg-terracotta-600 text-white' : 'bg-cream-50 text-espresso-700 hover:bg-cream-100'
                  }`}
                >
                  Compra directa
                </button>
              </div>

              {createMode === 'chain' && (
                <>
                  <div>
                    <label htmlFor="roast-source" className="block text-sm font-medium text-espresso-700 mb-1">
                      Lote de selección de origen *
                    </label>
                    <select
                      id="roast-source"
                      name="source_selection_lot_id"
                      value={form.source_selection_lot_id}
                      onChange={handleSourceChange}
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      required
                    >
                      <option value="">Selecciona un lote</option>
                      {selectionLots.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.lot_code} · {Number(l.kg_remaining).toFixed(2)} kg disponibles
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="roast-kg" className="block text-sm font-medium text-espresso-700 mb-1">
                        Kg de entrada *
                      </label>
                      <input
                        id="roast-kg"
                        type="number"
                        name="kg_input"
                        value={form.kg_input}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        required
                      />
                    </div>
                    <div>
                      <label htmlFor="roast-cost" className="block text-sm font-medium text-espresso-700 mb-1">
                        Costo de tueste por kg
                      </label>
                      <input
                        id="roast-cost"
                        type="number"
                        name="cost_per_kg_input"
                        value={form.cost_per_kg_input}
                        onChange={handleChange}
                        step="0.01"
                        min="0"
                        placeholder="Tarifa de tueste (Insumos)"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                  </div>
                </>
              )}

              {createMode === 'direct' && (
                <>
                  <div>
                    <label htmlFor="roast-direct-kg" className="block text-sm font-medium text-espresso-700 mb-1">
                      Kg Comprados *
                    </label>
                    <input
                      id="roast-direct-kg"
                      type="number"
                      name="kg_purchased"
                      value={directForm.kg_purchased}
                      onChange={handleDirectChange}
                      step="0.01"
                      min="0"
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="roast-direct-price" className="block text-sm font-medium text-espresso-700 mb-1">
                      Precio / kg
                    </label>
                    <input
                      id="roast-direct-price"
                      type="number"
                      name="price_per_kg"
                      value={directForm.price_per_kg}
                      onChange={handleDirectChange}
                      step="0.01"
                      min="0"
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="roast-direct-assembly" className="block text-sm font-medium text-espresso-700 mb-1">
                        Acopio
                      </label>
                      <input
                        id="roast-direct-assembly"
                        type="number"
                        name="assembly_cost"
                        value={directForm.assembly_cost}
                        onChange={handleDirectChange}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="roast-direct-freight" className="block text-sm font-medium text-espresso-700 mb-1">
                        Flete
                      </label>
                      <input
                        id="roast-direct-freight"
                        type="number"
                        name="freight_cost"
                        value={directForm.freight_cost}
                        onChange={handleDirectChange}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="roast-direct-storage" className="block text-sm font-medium text-espresso-700 mb-1">
                        Almacenaje
                      </label>
                      <input
                        id="roast-direct-storage"
                        type="number"
                        name="storage_cost"
                        value={directForm.storage_cost}
                        onChange={handleDirectChange}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="roast-direct-supplier" className="block text-sm font-medium text-espresso-700 mb-1">
                      Proveedor
                    </label>
                    <select
                      id="roast-direct-supplier"
                      name="supplier_id"
                      value={directForm.supplier_id}
                      onChange={handleDirectChange}
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    >
                      <option value="">Sin proveedor</option>
                      {suppliers.map((s) => (
                        <option key={s.id} value={s.id} disabled={!s.is_active}>
                          {s.name}{!s.is_active ? ' (inactivo)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="roast-direct-status" className="block text-sm font-medium text-espresso-700 mb-1">
                      Estado
                    </label>
                    <select
                      id="roast-direct-status"
                      name="status"
                      value={directForm.status}
                      onChange={handleDirectChange}
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    >
                      <option value="at_rest">En Almacén</option>
                      <option value="in_transit">En Tránsito</option>
                    </select>
                  </div>
                  {directForm.status === 'in_transit' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="roast-direct-origin" className="block text-sm font-medium text-espresso-700 mb-1">
                          Almacén origen
                        </label>
                        <select
                          id="roast-direct-origin"
                          name="origin_warehouse_id"
                          value={directForm.origin_warehouse_id}
                          onChange={handleDirectChange}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        >
                          <option value="">Selecciona</option>
                          {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>
                              {w.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="roast-direct-eta" className="block text-sm font-medium text-espresso-700 mb-1">
                          ETA
                        </label>
                        <input
                          id="roast-direct-eta"
                          type="date"
                          name="transit_eta"
                          value={directForm.transit_eta}
                          onChange={handleDirectChange}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              <div>
                <label htmlFor="roast-warehouse" className="block text-sm font-medium text-espresso-700 mb-1">
                  Almacén *
                </label>
                <select
                  id="roast-warehouse"
                  name="warehouse_id"
                  value={createMode === 'chain' ? form.warehouse_id : directForm.warehouse_id}
                  onChange={createMode === 'chain' ? handleChange : handleDirectChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                >
                  <option value="">Selecciona un almacén</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id} disabled={!w.is_active}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="roast-notes" className="block text-sm font-medium text-espresso-700 mb-1">
                  Notas
                </label>
                <textarea
                  id="roast-notes"
                  name="notes"
                  value={createMode === 'chain' ? form.notes : directForm.notes}
                  onChange={createMode === 'chain' ? handleChange : handleDirectChange}
                  rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Editar Lote {editingBatch.batch_code}</h2>
            </div>
            <form onSubmit={handleEditSubmit} className="px-6 py-4 space-y-4">
              {editError && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm" role="alert">
                  {editError}
                </div>
              )}
              <div>
                <label htmlFor="edit-roast-warehouse" className="block text-sm font-medium text-espresso-700 mb-1">
                  Almacén
                </label>
                <select
                  id="edit-roast-warehouse"
                  value={editForm.warehouse_id}
                  onChange={(e) => setEditForm({ ...editForm, warehouse_id: e.target.value })}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                >
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </div>
              {editingBatch.status === 'pending' ? (
                <div>
                  <label htmlFor="edit-roast-kg-input" className="block text-sm font-medium text-espresso-700 mb-1">
                    Kg de entrada
                  </label>
                  <input
                    id="edit-roast-kg-input"
                    type="number"
                    value={editForm.kg_input}
                    onChange={(e) => setEditForm({ ...editForm, kg_input: e.target.value })}
                    step="0.01"
                    min="0"
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    required
                  />
                </div>
              ) : (
                <div>
                  <label htmlFor="edit-roast-kg-output" className="block text-sm font-medium text-espresso-700 mb-1">
                    Kg de salida
                  </label>
                  <input
                    id="edit-roast-kg-output"
                    type="number"
                    value={editForm.kg_output}
                    onChange={(e) => setEditForm({ ...editForm, kg_output: e.target.value })}
                    step="0.01"
                    min="0"
                    disabled={!canEditKgOutput(editingBatch)}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500 disabled:bg-cream-100 disabled:text-espresso-400"
                    required
                  />
                  {!canEditKgOutput(editingBatch) && (
                    <p className="mt-1 text-xs text-espresso-500">
                      No se puede editar: ya se generaron lotes de mezclado a partir de este lote.
                    </p>
                  )}
                </div>
              )}
              {editingBatch.source_selection_lot_id === null && (
                <>
                  <div>
                    <label htmlFor="edit-roast-price" className="block text-sm font-medium text-espresso-700 mb-1">
                      Precio / kg
                    </label>
                    <input
                      id="edit-roast-price"
                      type="number"
                      value={editForm.price_per_kg}
                      onChange={(e) => setEditForm({ ...editForm, price_per_kg: e.target.value })}
                      step="0.01"
                      min="0"
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <label htmlFor="edit-roast-assembly" className="block text-sm font-medium text-espresso-700 mb-1">
                        Acopio
                      </label>
                      <input
                        id="edit-roast-assembly"
                        type="number"
                        value={editForm.assembly_cost}
                        onChange={(e) => setEditForm({ ...editForm, assembly_cost: e.target.value })}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="edit-roast-freight" className="block text-sm font-medium text-espresso-700 mb-1">
                        Flete
                      </label>
                      <input
                        id="edit-roast-freight"
                        type="number"
                        value={editForm.freight_cost}
                        onChange={(e) => setEditForm({ ...editForm, freight_cost: e.target.value })}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="edit-roast-storage" className="block text-sm font-medium text-espresso-700 mb-1">
                        Almacenaje
                      </label>
                      <input
                        id="edit-roast-storage"
                        type="number"
                        value={editForm.storage_cost}
                        onChange={(e) => setEditForm({ ...editForm, storage_cost: e.target.value })}
                        step="0.01"
                        min="0"
                        className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                      />
                    </div>
                  </div>
                </>
              )}
              {editingBatch.status !== 'pending' && (
                <>
                  <div>
                    <label htmlFor="edit-roast-status" className="block text-sm font-medium text-espresso-700 mb-1">
                      Estado
                    </label>
                    <select
                      id="edit-roast-status"
                      value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value as 'at_rest' | 'in_transit' })}
                      className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    >
                      <option value="at_rest">En Almacén</option>
                      <option value="in_transit">En Tránsito</option>
                    </select>
                  </div>
                  {editForm.status === 'in_transit' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="edit-roast-origin" className="block text-sm font-medium text-espresso-700 mb-1">
                          Almacén origen
                        </label>
                        <select
                          id="edit-roast-origin"
                          value={editForm.origin_warehouse_id}
                          onChange={(e) => setEditForm({ ...editForm, origin_warehouse_id: e.target.value })}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        >
                          <option value="">Selecciona</option>
                          {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>
                              {w.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor="edit-roast-eta" className="block text-sm font-medium text-espresso-700 mb-1">
                          ETA
                        </label>
                        <input
                          id="edit-roast-eta"
                          type="date"
                          value={editForm.transit_eta}
                          onChange={(e) => setEditForm({ ...editForm, transit_eta: e.target.value })}
                          className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                        />
                      </div>
                    </div>
                  )}
                </>
              )}
              <div>
                <label htmlFor="edit-roast-notes" className="block text-sm font-medium text-espresso-700 mb-1">
                  Notas
                </label>
                <textarea
                  id="edit-roast-notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setEditingBatch(null)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={editSaving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  {editSaving ? 'Guardando...' : 'Actualizar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Complete Modal */}
      {completingBatch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-sm mx-4">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">Completar Tostado {completingBatch.batch_code}</h2>
              <p className="text-sm text-espresso-500 mt-1">Kg de entrada: {Number(completingBatch.kg_input).toFixed(2)}</p>
            </div>
            <div className="px-6 py-4 space-y-4">
              {completeError && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm" role="alert">
                  {completeError}
                </div>
              )}
              <div>
                <label htmlFor="complete-kg-output" className="block text-sm font-medium text-espresso-700 mb-1">
                  Peso tostado medido (kg) *
                </label>
                <input
                  id="complete-kg-output"
                  type="number"
                  value={kgOutput}
                  onChange={(e) => setKgOutput(e.target.value)}
                  step="0.01"
                  min="0"
                  max={completingBatch.kg_input}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                  autoFocus
                />
              </div>
              {previewShrinkage !== null && (
                <div
                  className={`rounded-md p-3 text-sm ${
                    isOutOfRange(previewShrinkage) ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800'
                  }`}
                >
                  Merma resultante: <strong>{previewShrinkage.toFixed(1)}%</strong>
                  {isOutOfRange(previewShrinkage) && ' — fuera del rango esperado (15%-25%)'}
                </div>
              )}
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setCompletingBatch(null)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCompleteSubmit}
                  disabled={completeSaving}
                  className="px-4 py-2 bg-green-600 text-white rounded-md text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {completeSaving ? 'Guardando...' : 'Confirmar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
